import { Spacing } from '@persona-id/design-tokens';
import { snakeCase, startCase, uniq } from 'lodash/fp';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import {
  DEFAULT_AI_MODEL,
  ExtractionCategory,
  ExtractionPanelForm,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import {
  CompositeComparisonMethodType,
  DefinitionType,
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  GuidedExtractionRequestV3,
  GuidedPreset,
  MatchCriteria,
  MatchLevel,
  SubfieldMatchCriteria,
} from 'dashboard/models/document';
import { ComparisonMethodType, NormalizationMethodType } from 'dashboard/models/verification-check';
import { ErrorText } from 'design-system/components/FormControl';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { assertNever } from 'lib/utils/types';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface GuidedNameExtractionPanelProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  possibleInquiryFieldNames: string[];
  disabled?: boolean;
}

export const guidedExtractionRequestToPresetNameFormState = (
  extractionRequest: GuidedExtractionRequestV3,
): Partial<ExtractionPanelForm> => {
  const subfields = extractionRequest.subfields.map((s) => s['source-field']);
  return {
    version: '3.0',
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
    extractorType: ExtractorType.InquiryFieldComparison,
    fieldType: extractionRequest['field-type'],
    extractionCategory: ExtractionCategory.Name,
    matchRequirement:
      extractionRequest.preset === GuidedPreset.NameStrict
        ? MatchRequirement.Strict
        : MatchRequirement.Loose,
    nameFirstField: subfields[0],
    nameLastField: subfields[1],
  };
};

export const guidedPresetNameFormStateToExtractionRequest = (
  data: ExtractionPanelForm,
): ExtractionRequest => {
  const partialSubfieldMatchCriteria: SubfieldMatchCriteria = {
    'match-level': MatchLevel.Partial,
    'logic-composition': {
      type: 'logic_composition',
      'match-level-minimum': MatchLevel.Partial,
      operator: 'comparison',
      'comparison-method': {
        type: ComparisonMethodType.Ai,
        'match-level-minimum': MatchLevel.Partial,
        model: DEFAULT_AI_MODEL,
      },
      'normalization-methods': [],
      'augmentation-methods': [],
    },
  };
  const fullSubfieldMatchCriteria: SubfieldMatchCriteria = {
    'match-level': MatchLevel.Full,
    'logic-composition': {
      type: 'logic_composition',
      'match-level-minimum': MatchLevel.Partial,
      operator: 'comparison',
      'comparison-method': {
        type: ComparisonMethodType.StringSimilarity,
        'full-match-threshold': 0.9,
        'partial-match-threshold': 0.7,
        'max-char-difference': 4,
        'match-level-minimum': MatchLevel.Partial,
      },
      'normalization-methods': [
        {
          type: NormalizationMethodType.RemovePrefixes,
        },
        {
          type: NormalizationMethodType.RemoveSuffixes,
        },
        {
          type: NormalizationMethodType.LowercaseLetters,
        },
      ],
      'augmentation-methods': [],
    },
  };
  const partialMatchCriteria: MatchCriteria = {
    'match-level': MatchLevel.Partial,
    'subfields-composite-comparison-method': {
      type: CompositeComparisonMethodType.LogicComposition,
      operator: 'and',
      components: [
        {
          type: CompositeComparisonMethodType.LogicComposition,
          operator: 'comparison',
          components: [],
          'comparison-method': {
            type: CompositeComparisonMethodType.SubfieldMatchFound,
            'match-level-minimum': MatchLevel.Partial,
            subfield: data.nameFirstField,
          },
        },
        {
          type: CompositeComparisonMethodType.LogicComposition,
          operator: 'comparison',
          components: [],
          'comparison-method': {
            type: CompositeComparisonMethodType.SubfieldMatchFound,
            'match-level-minimum': MatchLevel.Partial,
            subfield: data.nameLastField,
          },
        },
      ],
    },
  };
  const fullMatchCriteria: MatchCriteria = {
    'match-level': MatchLevel.Full,
    'subfields-composite-comparison-method': {
      type: CompositeComparisonMethodType.LogicComposition,
      operator: 'and',
      components: [
        {
          type: CompositeComparisonMethodType.LogicComposition,
          operator: 'comparison',
          components: [],
          'comparison-method': {
            type: CompositeComparisonMethodType.SubfieldMatchFound,
            'match-level-minimum': MatchLevel.Full,
            subfield: data.nameFirstField,
          },
        },
        {
          type: CompositeComparisonMethodType.LogicComposition,
          operator: 'comparison',
          components: [],
          'comparison-method': {
            type: CompositeComparisonMethodType.SubfieldMatchFound,
            'match-level-minimum': MatchLevel.Full,
            subfield: data.nameLastField,
          },
        },
      ],
    },
  };
  switch (data.matchRequirement) {
    case MatchRequirement.Loose:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.NameLoose,
        subfields: [
          {
            'source-field': data.nameFirstField,
            'search-method': {
              method: 'full_string',
            },
            criteria: [partialSubfieldMatchCriteria, fullSubfieldMatchCriteria],
            'ai-model': DEFAULT_AI_MODEL,
          },
          {
            'source-field': data.nameLastField,
            'search-method': {
              method: 'full_string',
            },
            criteria: [partialSubfieldMatchCriteria, fullSubfieldMatchCriteria],
            'ai-model': DEFAULT_AI_MODEL,
          },
        ],
        'match-criteria': [partialMatchCriteria, fullMatchCriteria],
      };
    case MatchRequirement.Strict:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.NameStrict,
        subfields: [
          {
            'source-field': data.nameFirstField,
            'search-method': {
              method: 'full_string',
            },
            criteria: [fullSubfieldMatchCriteria],
          },
          {
            'source-field': data.nameLastField,
            'search-method': {
              method: 'full_string',
            },
            criteria: [fullSubfieldMatchCriteria],
          },
        ],
        'match-criteria': [fullMatchCriteria],
      };
    default:
      assertNever(data.matchRequirement as never);
  }
};

export const validateGuidedExtractionNamePanelForm = (
  data: ExtractionPanelForm,
  unconfigurableInquiryFieldNames: string[],
) => {
  const errorRequired = { type: 'required', message: 'Required' };
  const errors: FieldErrors<ExtractionPanelForm> = {};

  if (data?.nameFirstField == null) {
    errors.nameFirstField = errorRequired;
  }
  if (unconfigurableInquiryFieldNames.includes(snakeCase(data.nameFirstField))) {
    errors.nameFirstField = {
      type: 'type',
      message: 'The corresponding inquiry field is of an unconfigurable type',
    };
  }
  if (data?.nameLastField == null) {
    errors.nameLastField = errorRequired;
  }
  if (unconfigurableInquiryFieldNames.includes(snakeCase(data.nameLastField))) {
    errors.nameLastField = {
      type: 'type',
      message: 'The corresponding inquiry field is of an unconfigurable type',
    };
  }
  return errors;
};

const GuidedNameExtractionPanel = ({
  formReturn,
  possibleInquiryFieldNames,
  disabled = false,
}: GuidedNameExtractionPanelProps) => {
  const { formState, watch, control } = formReturn;
  const { errors } = formState;

  const usedInquiryFieldNames = watch(['nameFirstField', 'nameLastField']);

  return (
    <Section>
      <Paragraph weight='bold' color='primary'>
        Name extraction details
      </Paragraph>
      <Spacer height={1.5} />
      <StyledLabel>Name First Source Field</StyledLabel>
      <Controller
        name='nameFirstField'
        control={control}
        render={({ field }) => (
          <FieldWrapper>
            <Select
              {...field}
              placeholder='Select inquiry field to compare with'
              hasError={!!errors?.nameFirstField}
              disabled={disabled}
            >
              {uniq([...Object.values(possibleInquiryFieldNames), field.value])
                .filter((value) => value != null)
                .map((fieldName) => (
                  <Option
                    key={fieldName}
                    value={fieldName}
                    disabled={
                      fieldName !== field.value && usedInquiryFieldNames.includes(fieldName)
                    }
                  >
                    {startCase(fieldName)}
                  </Option>
                ))}
            </Select>
          </FieldWrapper>
        )}
      />
      <FieldErrorText message={errors?.nameFirstField?.message} />
      <Spacer height={1.5} />
      <StyledLabel>Name Last Source Field</StyledLabel>
      <Controller
        name='nameLastField'
        control={control}
        render={({ field }) => (
          <FieldWrapper>
            <Select
              {...field}
              placeholder='Select inquiry field to compare with'
              hasError={!!errors?.nameLastField}
              disabled={disabled}
            >
              {uniq([...Object.values(possibleInquiryFieldNames), field.value])
                .filter((value) => value != null)
                .map((fieldName) => (
                  <Option
                    key={fieldName}
                    value={fieldName}
                    disabled={
                      fieldName !== field.value && usedInquiryFieldNames.includes(fieldName)
                    }
                  >
                    {startCase(fieldName)}
                  </Option>
                ))}
            </Select>
          </FieldWrapper>
        )}
      />
      <FieldErrorText message={errors?.nameLastField?.message} />
    </Section>
  );
};

export default GuidedNameExtractionPanel;
